import { Box, Paper, Stack, Switch, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import React, { useCallback, useMemo, useState } from "react";
import { ReactComponent as Tags2Icon } from "../../../assets/icons/tags-2.svg";
import useGroupSettingsContext from "../../../hooks/context/useGroupSettingsContext";
import theme from "../../../theme";

const useStyles = makeStyles((theme) => ({
    card: {
        position: 'relative',
        minWidth: 265,
        width: 265,
        height: 425,
        padding: '24px 16px',
        borderRadius: 16,
        backgroundColor: '#fff',
        border: `1px solid #e2e8f0`,
        transition: 'all 300ms ease-in-out',
        transformOrigin: 'center center',
        "@media (max-height: 750px)": { transform: 'scale(0.95)' },
        "@media (max-height: 700px)": { transform: 'scale(0.9)' },
        "@media (max-height: 650px)": { transform: 'scale(0.85)' },
        "@media (max-height: 600px)": { transform: 'scale(0.8)' },
        "@media (max-height: 550px)": { transform: 'scale(0.75)' },
        "@media (max-height: 500px)": { transform: 'scale(0.7)' },
        "@media (max-height: 450px)": { transform: 'scale(0.65)' },
        "@media (max-height: 400px)": { transform: 'scale(0.6)' },
        "&:hover": {
            borderColor: '#1eb6a8',
            boxShadow: '0 8px 24px rgba(30, 182, 168, 0.12)',
        },
    },
    mainIcon: {
        width: 20,
        height: 20,
        stroke: '#1eb6a8',
        fill: 'none',
        strokeWidth: '2',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
    },
    mainIconContainer: {
        width: 40,
        height: 40,
        background: '#f0fdf9',
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px solid #d1f5ee',
        flexShrink: 0,
    },
    subIcon: {
        width: 24,
        height: 24,
        fill: theme.colors.neutral['400'],
    },
    leftBulletIcon: {
        width: 24,
        height: 24,
        fill: theme.colors.primary['300'],
        backgroundColor: '#fff',
        transform: 'translateX(-12px)',
    },
    longLine: {
        width: '100%',
        borderBottom: `3px solid ${theme.colors.primary['200']}`,
        marginLeft: '-16px',
        marginRight: '-12px',
        zIndex: 1,
    },
    rightArrowIcon: {
        width: 32,
        height: 32,
        fill: theme.colors.primary['300'],
        zIndex: 1,
    },
    title: {
        fontFamily: 'Lato',
        fontSize: '1.1rem',
        fontWeight: 600,
        color: '#1e293b',
    },
    label: {
        fontFamily: 'Lato',
        fontWeight: 600,
        fontSize: '0.8rem',
        color: '#1e293b',
    },
    subText: {
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        fontWeight: 400,
        color: theme.colors.neutral['500'],
    },
    link: {
        display: '-webkit-box',
        textDecoration: 'none',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'normal',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
        wordBreak: 'break-all',
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        cursor: 'pointer',
    },
    iconContainer: {
        width: 40,
        height: 40,
        backgroundColor: '#f0fdf9',
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px solid #d1f5ee'
    },
    icon: {
        fontSize: 20,
        color: '#1eb6a8'
    },
    description: {
        marginBottom: theme.spacing(3.5),
        lineHeight: 1.6
    },
    optionsContainer: {
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        gap: theme.spacing(1.25)
    },
    option: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1.25),
        border: '2px solid',
        borderRadius: 10,
        position: 'relative',
        transition: 'all 0.2s'
    },
    optionDefault: {
        backgroundColor: '#f8f9fc',
        borderColor: 'transparent',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: '#f0fdf9',
            borderColor: '#d1f5ee'
        }
    },
    optionSelected: {
        backgroundColor: '#f0fdf9',
        borderColor: '#1eb6a8',
        cursor: 'default',
        boxShadow: '0 0 0 3px rgba(30, 182, 168, 0.1)',
        '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            backgroundColor: '#1eb6a8',
            borderRadius: '10px 0 0 10px'
        },
        '&:hover': {
            backgroundColor: '#f8f9fc'
        }
    },
    optionEnabled: {
        cursor: 'default',
        '&:hover': {
            backgroundColor: '#f8f9fc',
            borderColor: 'transparent'
        }
    },
    optionInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1)
    },
    colorDot: {
        width: 10,
        height: 10,
        borderRadius: '50%',
        flexShrink: 0
    },
    switch: {
        width: 80,
        height: 36,
        padding: 0,
        transform: 'scale(0.6)',
        '& .MuiSwitch-switchBase': {
            padding: '6px',
            transitionDuration: '300ms',
            '&.Mui-checked': {
                transform: 'translateX(40px)',
                '& + .MuiSwitch-track': {
                    border: '2px solid #15b8a5',
                    backgroundColor: '#15b8a5',
                    opacity: 1,
                },
                '& .MuiSwitch-thumb': {
                    color: '#fff',
                },
            },
        },
        '& .MuiSwitch-thumb': {
            boxSizing: 'border-box',
            width: 24,
            height: 24,
            color: '#15b8a5',
        },
        '& .MuiSwitch-track': {
            borderRadius: '20px',
            border: '2px solid #15b8a5',
            backgroundColor: '#fff',
            opacity: 1,
        },
    },
    connector: {
        position: 'relative',
        top: '50%',
        right: 0,
        width: 30,
        height: 2,
        backgroundColor: '#cbd5e1',
        transform: 'translateY(-50%)',
        display: 'none',
        [theme.breakpoints.up('lg')]: {
            display: 'block'
        },
        '&::before': {
            content: '""',
            position: 'absolute',
            top: -4,
            right: -1,
            width: 0,
            height: 0,
            borderLeft: '6px solid #1eb6a8',
            borderTop: '5px solid transparent',
            borderBottom: '5px solid transparent'
        }
    },
}));

const EmailType: React.FC<{
    recentType?: string,
    panelMode?: boolean,
    onEmailType: React.Dispatch<React.SetStateAction<string>>
}> = ({ recentType, panelMode, onEmailType }) => {
    const classes = useStyles();
    const { emailTypeSettings, setEmailTypeSettings } = useGroupSettingsContext();
    const [selectedId, setSelectedId] = useState(1);

    const fieldMap: Record<number, keyof typeof emailTypeSettings> = useMemo(() => ({
        1: 'newDeal',
        2: 'companyUpdate',
        3: 'newFundOpportunity',
        4: 'sharedPipeline',
    }), []);

    const emailTypes = useMemo(() => [
        { id: 1, label: 'New Deal', color: theme.colors.blue['600'], enabled: emailTypeSettings.newDeal !== false, selected: selectedId === 1 },
        { id: 2, label: 'Company Update', color: theme.colors.warning['400'], enabled: emailTypeSettings.companyUpdate !== false, selected: selectedId === 2 },
        { id: 3, label: 'New Fund Opportunity', color: '#12b981', enabled: emailTypeSettings.newFundOpportunity !== false, selected: selectedId === 3 },
        { id: 4, label: 'Shared Pipeline', color: theme.colors.violet['600'], enabled: emailTypeSettings.sharedPipeline !== false, selected: selectedId === 4 }
    ], [emailTypeSettings, selectedId]);

    const handleToggle = useCallback((id: number) => {
        const field = fieldMap[id];
        if (field) {
            setEmailTypeSettings(prev => ({
                ...prev,
                [field]: !(prev[field] !== false),
            }));
        }
    }, [fieldMap, setEmailTypeSettings]);

    const handleSelect = useCallback((id: number) => {
        onEmailType(emailTypes.find(type => type.id === id)?.label || '');
        setSelectedId(id);
    }, [emailTypes, onEmailType]);

    const getOptionClassName = useCallback((type: { selected: boolean; enabled: boolean }) => {
        let classNames = [classes.option];

        if (type.selected) {
            classNames.push(classes.optionSelected);
        } else {
            classNames.push(classes.optionDefault);
        }
        if (type.enabled) {
            classNames.push(classes.optionEnabled);
        }

        return classNames.join(' ');
    }, [classes]);

    if (panelMode) {
        return (
            <Stack spacing={2} alignItems="center" justifyContent="center" width="100%" maxWidth={420} mx="auto" py={4}>
                <Typography fontFamily="Lato" fontSize="1rem" color="#64748b" textAlign="center">
                    {'Enable the email types you want Notissia to process'}
                </Typography>
                <Box className={classes.optionsContainer}>
                    {emailTypes.map((type) => (
                        <Paper className={`${classes.option} ${classes.optionDefault}`}
                            elevation={0} key={type.id}
                            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box className={classes.optionInfo}>
                                <Box className={classes.colorDot}
                                    style={{ backgroundColor: type.color }} />
                                <Typography className={classes.label}
                                    variant="body2" color="textPrimary" fontWeight={600}>
                                    {type.label}
                                </Typography>
                            </Box>
                            <Switch className={classes.switch}
                                checked={type.enabled}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    handleToggle(type.id);
                                }} size="small" />
                        </Paper>
                    ))}
                </Box>
            </Stack>
        );
    }

    return (<>
        <Stack direction="row" alignItems="center" justifyContent="flex-start" width="100%" >
            <Stack className={classes.card} spacing={2} alignItems="flex-start">
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start">
                    <Box className={classes.mainIconContainer}>
                        <Tags2Icon className={classes.mainIcon} />
                    </Box>
                    <Typography className={classes.title}>
                        {'Email Type'}
                    </Typography>
                </Stack>
                <Typography fontFamily="Lato" fontSize="0.85rem" color={`#64748b`}>
                    {'Smart classification into deal types'}
                </Typography>
                <Stack spacing={1} alignItems="flex-start" width="100%">
                    <Box className={classes.optionsContainer}>
                        {emailTypes.map((type) => (
                            <Paper className={getOptionClassName(type)}
                                onClick={() => handleSelect(type.id)}
                                style={{ display: !recentType || recentType.toLowerCase() === type.label.toLowerCase() ? 'flex' : 'none' }}
                                elevation={0} key={type.id}>
                                <Box className={classes.optionInfo}>
                                    <Box className={classes.colorDot}
                                        style={{ backgroundColor: type.color }} />
                                    <Typography className={classes.label}
                                        variant="body2" color="textPrimary">
                                        {type.label}
                                    </Typography>
                                </Box>
                                <Switch className={classes.switch}
                                    checked={type.enabled}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        handleToggle(type.id);
                                    }} size="small" />
                            </Paper>
                        ))}
                    </Box>
                </Stack>
            </Stack>
            <Box className={classes.connector} />
        </Stack>
    </>);
}

export default EmailType;
